import { useState, useEffect, useRef } from 'react';
import { TEAM_COLORS, DEFAULT_TEAM_COLOR } from '../constants';
import './plays-view.css';

function scoringTeamId(play, plays, game) {
  const idx = plays.indexOf(play);
  const prev = idx > 0 ? plays[idx - 1] : null;
  const prevHome = prev ? prev.homeScore : 0;
  const prevAway = prev ? prev.awayScore : 0;
  if (play.homeScore > prevHome) return game.homeTeam.id;
  if (play.awayScore > prevAway) return game.awayTeam.id;
  return null;
}

function ordinal(n) {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  if (n === 4) return '4th';
  return `${n}th`;
}

function formatKickoff(scheduledTime) {
  const date = new Date(scheduledTime);
  const datePart = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} · ${timePart}`;
}

function WinProbGraph({ plays, game, selectedModel }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(680);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const height = 450;
  const padding = { top: 30, right: 20, bottom: 40, left: 55 };

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const playsWithProb = plays.filter(p => {
    if (!p.winProbability) return false;
    const prob = p.winProbability[selectedModel];
    return prob && prob.home !== null;
  });
  if (playsWithProb.length === 0) return null;

  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const midY = padding.top + chartH / 2;

  const homeColor = (TEAM_COLORS[game.homeTeam.id] || DEFAULT_TEAM_COLOR).primary;
  const awayColor = (TEAM_COLORS[game.awayTeam.id] || DEFAULT_TEAM_COLOR).primary;

  const points = playsWithProb.map((play, i) => {
    const x = padding.left + (i / (playsWithProb.length - 1 || 1)) * chartW;
    const y = padding.top + (1 - play.winProbability[selectedModel].home) * chartH;
    return { x, y };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  const homeClipPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
    + ` L${points[points.length - 1].x},${midY} L${points[0].x},${midY} Z`;

  const awayClipPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
    + ` L${points[points.length - 1].x},${midY} L${points[0].x},${midY} Z`;

  const yTicks = [
    { pct: 100, label: '100%' },
    { pct: 75, label: '75%' },
    { pct: 50, label: '50%' },
    { pct: 25, label: '75%' },
    { pct: 0, label: '100%' },
  ];

  function handleMouseMove(e) {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const relX = mouseX - padding.left;
    if (relX < 0 || relX > chartW) {
      setHoveredIndex(null);
      return;
    }
    const index = Math.round((relX / chartW) * (playsWithProb.length - 1));
    setHoveredIndex(Math.max(0, Math.min(index, playsWithProb.length - 1)));
  }

  const hoveredPlay = hoveredIndex !== null ? playsWithProb[hoveredIndex] : null;
  const hoveredPoint = hoveredIndex !== null ? points[hoveredIndex] : null;

  return (
    <div className="graph-wrapper">
      <div className="graph-container" ref={containerRef}>
        <svg
          width={width}
          height={height}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          {yTicks.map((tick, i) => {
            const y = padding.top + (1 - tick.pct / 100) * chartH;
            return (
              <g key={i}>
                <line
                  x1={padding.left} y1={y} x2={width - padding.right} y2={y}
                  stroke={tick.pct === 50 ? '#666' : '#2a2a4a'}
                  strokeWidth={tick.pct === 50 ? 1.5 : 1}
                  strokeDasharray={tick.pct === 50 ? '6,4' : 'none'}
                />
                <text x={padding.left - 8} y={y + 5} textAnchor="end" fill="#888" fontSize="16">
                  {tick.label}
                </text>
              </g>
            );
          })}

          <clipPath id="clip-home">
            <rect x={padding.left} y={padding.top} width={chartW} height={chartH / 2} />
          </clipPath>
          <clipPath id="clip-away">
            <rect x={padding.left} y={midY} width={chartW} height={chartH / 2} />
          </clipPath>

          <path d={homeClipPath} fill={homeColor} opacity={0.25} clipPath="url(#clip-home)" />
          <path d={awayClipPath} fill={awayColor} opacity={0.25} clipPath="url(#clip-away)" />

          <path d={linePath} fill="none" stroke="#444" strokeWidth={2} />

          {playsWithProb.map((play, i) => {
            if (!play.scoringPlay) return null;
            const teamId = scoringTeamId(play, playsWithProb, game);
            const color = (TEAM_COLORS[teamId] || DEFAULT_TEAM_COLOR).primary;
            return (
              <circle
                key={i}
                cx={points[i].x}
                cy={points[i].y}
                r={6}
                fill={color}
                stroke="#fff"
                strokeWidth={2}
              />
            );
          })}

          {hoveredPoint && (
            <g>
              <line
                x1={hoveredPoint.x} y1={padding.top}
                x2={hoveredPoint.x} y2={height - padding.bottom}
                stroke="#888" strokeWidth={1} strokeDasharray="4,3"
              />
              <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r={7} fill="#fff" stroke="#444" strokeWidth={2.5} />
            </g>
          )}

          <text x={width - padding.right} y={padding.top - 8} textAnchor="end" fill={homeColor} fontSize="18" fontWeight="600">
            {game.homeTeam.name}
          </text>
          <text x={width - padding.right} y={height - padding.bottom + 24} textAnchor="end" fill={awayColor} fontSize="18" fontWeight="600">
            {game.awayTeam.name}
          </text>
        </svg>
      </div>

      <div className="graph-sidebar">
        {hoveredPlay ? (
          <div className="graph-tooltip">
            <div className="tooltip-meta">
              <span className="play-quarter">Q{hoveredPlay.quarter}</span>
              <span className="play-clock">{hoveredPlay.clock}</span>
            </div>
            <div className="tooltip-prob">
              <span style={{ color: awayColor }}>{game.awayTeam.name}</span>
              <span style={{ color: awayColor }}>{hoveredPlay.awayScore}</span>
              <span style={{ color: awayColor }}>{Math.round(hoveredPlay.winProbability[selectedModel].away * 100)}%</span>
              <span style={{ color: homeColor }}>{game.homeTeam.name}</span>
              <span style={{ color: homeColor }}>{hoveredPlay.homeScore}</span>
              <span style={{ color: homeColor }}>{Math.round(hoveredPlay.winProbability[selectedModel].home * 100)}%</span>
            </div>
            {hoveredPlay.possession && (
              <div className="tooltip-situation">
                {hoveredPlay.possession} ball
                {hoveredPlay.down && hoveredPlay.distance != null && ` · ${ordinal(hoveredPlay.down)} & ${hoveredPlay.distance}`}
                {hoveredPlay.yardLine != null && ` · ${hoveredPlay.yardLine <= 50 ? hoveredPlay.yardLine : 100 - hoveredPlay.yardLine} yd line`}
              </div>
            )}
            <p className="tooltip-description">{hoveredPlay.description}</p>
          </div>
        ) : (
          <div className="graph-tooltip graph-tooltip-empty">
            <p className="tooltip-description">Hover over the graph to see play details</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PlaysView({ game, plays, showGraph, onToggleGraph, loading, error, onBack }) {
  const [selectedModel, setSelectedModel] = useState('base');
  const lastPlay = plays.length > 0 ? plays[plays.length - 1] : null;
  const isFuture = !!game.scheduledTime && new Date(game.scheduledTime) > new Date();
  const awayColor = (TEAM_COLORS[game.awayTeam.id] || DEFAULT_TEAM_COLOR).primary;
  const homeColor = (TEAM_COLORS[game.homeTeam.id] || DEFAULT_TEAM_COLOR).primary;

  return (
    <div className={`app${showGraph ? ' app-wide' : ''}`}>
      <button className="back-button" onClick={onBack}>Back to Games</button>
      <div className="title-row">
        <div className="title-teams">
          <div className="title-team">
            <span className="title-team-name">{game.awayTeam.name}</span>
            {lastPlay && <span className="title-score">{lastPlay.awayScore}</span>}
          </div>
          <div className="title-team">
            <span className="title-team-name">{game.homeTeam.name}</span>
            {lastPlay && <span className="title-score">{lastPlay.homeScore}</span>}
          </div>
        </div>
        {!loading && !error && !isFuture && plays.length > 0 && (
          <div className="title-controls">
            <div className="model-toggle">
              <button
                className={`model-toggle-button${selectedModel === 'base' ? ' active' : ''}`}
                onClick={() => setSelectedModel('base')}
              >
                Base
              </button>
              <button
                className={`model-toggle-button${selectedModel === 'downAndDistance' ? ' active' : ''}`}
                onClick={() => setSelectedModel('downAndDistance')}
              >
                Down &amp; Distance
              </button>
              <button
                className={`model-toggle-button${selectedModel === 'priors' ? ' active' : ''}`}
                onClick={() => setSelectedModel('priors')}
              >
                Priors
              </button>
            </div>
            <button className="graph-button" onClick={onToggleGraph}>
              {showGraph ? 'Play by Play' : 'Graph'}
            </button>
          </div>
        )}
      </div>

      {loading && <p className="loading">Loading plays...</p>}
      {error && <p className="error">Error: {error}</p>}

      {!loading && !error && isFuture && (
        <div className="future-game-notice">
          <p className="future-game-headline">This game hasn't started yet.</p>
          <p className="future-game-detail">Kickoff: {formatKickoff(game.scheduledTime)}</p>
          {game.preGameWinProbability && (
            <div className="future-game-prediction">
              <p className="future-game-prediction-label">Predicted win probability</p>
              <div className="win-prob-bar">
                <div
                  className="win-prob-fill"
                  style={{ width: `${Math.round(game.preGameWinProbability.away * 100)}%`, background: awayColor }}
                >
                  {Math.round(game.preGameWinProbability.away * 100)}%
                </div>
                <div
                  className="win-prob-fill"
                  style={{ width: `${Math.round(game.preGameWinProbability.home * 100)}%`, background: homeColor }}
                >
                  {Math.round(game.preGameWinProbability.home * 100)}%
                </div>
              </div>
              <div className="win-prob-labels">
                <span>{game.awayTeam.name}</span>
                <span>{game.homeTeam.name}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && !error && !isFuture && showGraph && (
        <WinProbGraph plays={plays} game={game} selectedModel={selectedModel} />
      )}

      {!loading && !error && !isFuture && !showGraph && (
        <ul className="plays-list">
          {plays.map(play => (
            <li
              key={play.playId}
              className={`play-item${play.scoringPlay ? ' scoring' : ''}`}
              style={play.scoringPlay ? { borderLeftColor: (TEAM_COLORS[scoringTeamId(play, plays, game)] || DEFAULT_TEAM_COLOR).primary } : undefined}
            >
              <div className="play-meta">
                <span className="play-quarter">Q{play.quarter}</span>
                <span className="play-clock">{play.clock}</span>
                <span className="play-score">
                  {game.awayTeam.name} {play.awayScore} - {play.homeScore} {game.homeTeam.name}
                </span>
              </div>
              {play.possession && (
                <div className="play-situation">
                  <span className="play-possession">{play.possession} ball</span>
                  {play.down && play.distance != null && (
                    <span className="play-down">{ordinal(play.down)} & {play.distance}</span>
                  )}
                  {play.yardLine != null && (
                    <span className="play-yardline">at {play.yardLine <= 50 ? play.yardLine : 100 - play.yardLine} yd line</span>
                  )}
                </div>
              )}
              {play.winProbability && (() => {
                const prob = play.winProbability[selectedModel];
                if (!prob || prob.home === null) return null;
                return (
                <div className="play-win-prob">
                  <div className="win-prob-bar">
                    <div
                      className="win-prob-fill"
                      style={{
                        width: `${Math.round(prob.away * 100)}%`,
                        background: (TEAM_COLORS[game.awayTeam.id] || DEFAULT_TEAM_COLOR).primary,
                      }}
                    >
                      {Math.round(prob.away * 100)}%
                    </div>
                    <div
                      className="win-prob-fill"
                      style={{
                        width: `${Math.round(prob.home * 100)}%`,
                        background: (TEAM_COLORS[game.homeTeam.id] || DEFAULT_TEAM_COLOR).primary,
                      }}
                    >
                      {Math.round(prob.home * 100)}%
                    </div>
                  </div>
                  <div className="win-prob-labels">
                    <span>{game.awayTeam.name}</span>
                    <span>{game.homeTeam.name}</span>
                  </div>
                </div>
                );
              })()}
              <p className="play-description">{play.description}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
